import React, { useEffect, useState } from "react";
import { api, fmtEuro } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, FileSpreadsheet, FileText, TrendingUp } from "lucide-react";
import { toast } from "sonner";

export default function Comptabilite() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (y) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/accounting/summary?year=${y}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(year); }, [year]);

  const exportCSV = () => {
    if (!data) return;
    const rows = [
      ["Mois", "CA net (€)", "Dépenses (€)", "Résultat (€)", "Courses"],
      ...data.monthly.map((m) => [m.month, m.ca, m.depenses, m.resultat, m.courses]),
      [],
      ["Année", data.year],
      ["CA brut (€)", data.ca_brut],
      ["CA net (€)", data.ca_net],
      ["Dépenses (€)", data.depenses],
      ["Résultat (€)", data.resultat],
    ];
    const csv = rows.map((r) => r.join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `comptabilite-${data.year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export CSV téléchargé");
  };

  const printPDF = () => {
    window.print();
  };

  if (loading || !data) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const years = [year - 1, year, year + 1].filter((y) => y >= 2024 && y <= new Date().getFullYear() + 1);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Comptabilité micro-entrepreneur</div>
          <h1 className="font-manrope text-3xl md:text-4xl font-extrabold">Livre des recettes & dépenses</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-border overflow-hidden">
            {years.map((y) => (
              <button
                key={y}
                onClick={() => setYear(y)}
                className={`px-3 py-2 text-sm ${y === year ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-muted/60"}`}
                data-testid={`btn-year-${y}`}
              >
                {y}
              </button>
            ))}
          </div>
          <Button variant="secondary" onClick={exportCSV} data-testid="btn-export-csv">
            <FileSpreadsheet className="h-4 w-4 mr-2" /> CSV
          </Button>
          <Button variant="secondary" onClick={printPDF} data-testid="btn-export-pdf">
            <FileText className="h-4 w-4 mr-2" /> PDF
          </Button>
        </div>
      </div>

      {/* Totaux annuels */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Total label="CA brut" value={fmtEuro(data.ca_brut)} testid="total-ca-brut" />
        <Total label="CA net (recettes)" value={fmtEuro(data.ca_net)} testid="total-ca-net" />
        <Total label="Dépenses" value={fmtEuro(data.depenses)} tone="destructive" testid="total-depenses" />
        <Total label="Résultat" value={fmtEuro(data.resultat)} tone={data.resultat >= 0 ? "success" : "destructive"} testid="total-resultat" />
      </div>

      {/* Table mensuelle */}
      <div className="card-surface p-5" data-testid="table-accounting">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-4 w-4 text-primary" />
          <h3 className="font-manrope font-semibold">Détail mensuel {year}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left px-2 py-2">Mois</th>
                <th className="text-right px-2 py-2">Courses</th>
                <th className="text-right px-2 py-2">Recettes</th>
                <th className="text-right px-2 py-2">Dépenses</th>
                <th className="text-right px-2 py-2">Résultat</th>
              </tr>
            </thead>
            <tbody>
              {data.monthly.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">Aucune donnée pour {year}</td></tr>
              ) : data.monthly.map((m) => (
                <tr key={m.month} className="border-b border-border/60 hover:bg-muted/40">
                  <td className="px-2 py-2 num font-medium">{m.month}</td>
                  <td className="px-2 py-2 text-right num">{m.courses}</td>
                  <td className="px-2 py-2 text-right num">{fmtEuro(m.ca)}</td>
                  <td className="px-2 py-2 text-right num text-destructive">{fmtEuro(m.depenses)}</td>
                  <td className={`px-2 py-2 text-right num font-semibold ${m.resultat >= 0 ? "text-[hsl(var(--success))]" : "text-destructive"}`}>{fmtEuro(m.resultat)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Total({ label, value, tone = "primary", testid }) {
  const tones = {
    primary: "border-primary/25 bg-primary/5",
    destructive: "border-destructive/25 bg-destructive/5",
    success: "border-[hsl(var(--success))]/25 bg-[hsl(var(--success))]/5",
  };
  return (
    <div className={`rounded-xl border ${tones[tone]} p-4`} data-testid={testid}>
      <div className="overline mb-2">{label}</div>
      <div className="font-manrope font-extrabold text-xl num">{value}</div>
    </div>
  );
}
